// ReviewSummaryCard — the lease "Review summary" header: every VARIABLE detail
// of one lease, for staff, above the lease itself.
//
//   Miles, 2026-09-24: "anyone from welcomehps.com that gets a link for the
//   lease or views the lease in builder should see the full header version of
//   the lease with the lease below it."
//
// ONE card, two mounts, so they cannot drift:
//   - pages/SignPage       WHP staff on their /sign/:uuid page (server-gated —
//                          only a staff view carries `reviewSummary`)
//   - pages/ConfigurePage  the lease builder, above the lease preview
//
// Visually distinct (accent left border) and explicitly labelled as reference
// material — it is NOT part of the lease. Every value is computed server-side
// with the same helpers that print the PDF (apps/api/src/lib/
// buildLeaseSignSummary.ts); nothing here recomputes money.

import type { ReviewSummary, ReviewSummaryRow } from '@/lib/signClient';

function fmtDate(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Long values (Other terms, utility prose) stack label-over-value; short
 *  values render as a compact two-column row. */
function isLongValue(r: ReviewSummaryRow): boolean {
  return r.value.length > 72 || r.value.includes('\n');
}

function ReviewSummaryRows({ rows }: { rows: ReviewSummaryRow[] }): React.JSX.Element {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <tbody>
        {rows.map((r, i) => {
          const weight = r.emphasis ? 600 : 400;
          const borderTop = i === 0 ? 'none' : '1px solid var(--border, rgba(0,0,0,0.08))';
          if (isLongValue(r)) {
            return (
              <tr key={`${r.label}-${i}`}>
                <td colSpan={2} style={{ padding: 'var(--space-2) 0', borderTop }}>
                  <div
                    style={{
                      fontSize: 'var(--text-sm)',
                      color: 'var(--fg-muted)',
                      fontWeight: r.emphasis ? 600 : 400,
                    }}
                  >
                    {r.label}
                  </div>
                  <div
                    style={{
                      marginTop: 'var(--space-1)',
                      color: 'var(--fg)',
                      fontWeight: weight,
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {r.value}
                  </div>
                </td>
              </tr>
            );
          }
          return (
            <tr key={`${r.label}-${i}`}>
              <td
                style={{
                  padding: 'var(--space-2) var(--space-3) var(--space-2) 0',
                  borderTop,
                  fontSize: 'var(--text-sm)',
                  color: r.emphasis ? 'var(--fg)' : 'var(--fg-muted)',
                  fontWeight: r.emphasis ? 600 : 400,
                  verticalAlign: 'top',
                  width: '46%',
                }}
              >
                {r.label}
              </td>
              <td
                style={{
                  padding: 'var(--space-2) 0',
                  borderTop,
                  color: 'var(--fg)',
                  fontWeight: weight,
                  textAlign: 'right',
                  verticalAlign: 'top',
                  overflowWrap: 'anywhere',
                }}
              >
                {r.value}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** The sign page's framing: a generated version, the signed lease below it. */
export function versionSummaryIntro(summary: ReviewSummary): string {
  return `Every variable detail of this document, recomputed from the exact snapshot it was generated from${
    summary.generatedAt ? ` on ${fmtDate(summary.generatedAt)}` : ''
  }. The signed lease below is the only binding document.`;
}

/** The builder's framing for a GENERATED version (State B: no changes since). */
export function generatedVersionSummaryIntro(generatedAt: string | null): string {
  const on = fmtDate(generatedAt);
  return `Every variable detail of the generated lease${
    on ? ` (${on})` : ''
  }, from the exact snapshot it was printed from. The generated and signed lease is the only binding document.`;
}

/** The builder's framing for the SAVED draft: what generate would print now. */
export function draftSummaryIntro(asOf: string | null): string {
  const when = asOf ? new Date(asOf) : null;
  const stamp =
    when && !Number.isNaN(when.getTime())
      ? ` as of the last save (${when.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })})`
      : ' as of the last save';
  return `Every variable detail of this draft${stamp}, computed the same way the generated lease will print it. The generated and signed lease is the only binding document.`;
}

/** The builder's advisory while an accommodation request is open and the lease suspends animal charges (staff copy, not DRAFT). */
export const RRA_REQUEST_OPEN_BANNER = 'Accommodation request open — animal charges are suspended while we decide';
/** …and when the request is open but nothing is suspended (the household has no animal hold). */
export const RRA_REQUEST_OPEN_PLAIN = 'Accommodation request open.';

/**
 * RRA v2.2 (WEL-180): an accommodation request is open on this household. While it is open on an animal
 * request the lease suspends animal charges for ALL of the household's animals until it is decided, and
 * the advisory says so (`animalChargesSuspended`). A request no longer names an animal (the animals are
 * chosen at approval), so the banner names none: true → the suspension sentence; false or absent →
 * just that a request is open.
 */
export function rraRequestOpenBanner(animalChargesSuspended?: boolean): string {
  return animalChargesSuspended === true ? RRA_REQUEST_OPEN_BANNER : RRA_REQUEST_OPEN_PLAIN;
}

export function ReviewSummaryCard({
  summary,
  intro,
  style,
  rraRequestOpen = null,
}: {
  summary: ReviewSummary;
  /** Overrides the sign page's version framing (the builder's draft framing). */
  intro?: string;
  /** Outer spacing for the mount (the card owns nothing but its bottom gap). */
  style?: React.CSSProperties;
  /** RRA v2.1: an accommodation request is open on this household (builder only). `animalChargesSuspended`
   *  (RRA v2.2) picks the sentence. Any `animalNames` the API still sends (a pre-v2.2 request) are ignored
   *  here — the banner names no animal. */
  rraRequestOpen?: { animalNames?: string[]; animalChargesSuspended?: boolean; animalRequestOpen?: boolean; markableAnimals?: Array<{ key: string; name: string; type?: 'dog' | 'cat' | 'other' }> } | null;
}): React.JSX.Element {
  return (
    <section
      className="card"
      data-section="review-summary"
      style={{
        marginBottom: 'var(--space-5)',
        borderLeft: '3px solid var(--accent)',
        ...style,
      }}
    >
      {rraRequestOpen && (
        <div
          className="alert"
          data-tone="info"
          role="status"
          data-section="rra-request-open"
          style={{ marginBottom: 'var(--space-3)' }}
        >
          <div className="alert-body">{rraRequestOpenBanner(rraRequestOpen.animalChargesSuspended)}</div>
        </div>
      )}
      <h2
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 'var(--text-lg)',
          fontWeight: 400,
          color: 'var(--fg)',
          margin: 0,
        }}
      >
        Review summary — for your reference, not part of the lease
      </h2>
      <p
        style={{
          marginTop: 'var(--space-2)',
          color: 'var(--fg-muted)',
          fontSize: 'var(--text-sm)',
        }}
      >
        {intro ?? versionSummaryIntro(summary)}
      </p>
      <div style={{ marginTop: 'var(--space-4)' }}>
        {summary.sections.map((sec) => (
          <details key={sec.title} open style={{ marginBottom: 'var(--space-3)' }}>
            <summary
              style={{
                cursor: 'pointer',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-sm)',
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'var(--fg-muted)',
                padding: 'var(--space-2) 0',
              }}
            >
              {sec.title}
            </summary>
            <ReviewSummaryRows rows={sec.rows} />
          </details>
        ))}
      </div>
    </section>
  );
}
